/**
 * Polymarket-aligned exclusive odds (display only).
 * Core math: exclusive-outcome-odds-core.js
 * Collection apply: uses market outcomes / outcomePrices helpers.
 */

import { isMarketResolved, parseMarketOutcomes, parseOutcomePrices } from '@/utils/market-outcomes';
import { serializeOutcomePrices } from '@/utils/market-collection';
import { isBinaryOutcomes } from '@/utils/market-variant';
import {
  complementBinaryYes,
  DEFAULT_HALF,
  isNestedThresholdQuestion,
  isSpreadOrTotalPair,
  scaleExclusiveYesIfOverflow,
  toProb,
} from '@/domain/market/exclusive-outcome-odds-core.js';

export {
  buyCentsFromLiveAsk,
  clamp01,
  complementBinaryYes,
  DEFAULT_HALF,
  exclusiveQuestionStem,
  exclusiveSumOverflow,
  impliedProbFromLiveBook,
  isNestedThresholdQuestion,
  isSpreadOrTotalPair,
  looksLikeDefaultHalf,
  normalizeMultiOutcomeProbs,
  redistributeExclusive,
  scaleExclusiveYesIfOverflow,
  toProb,
} from '@/domain/market/exclusive-outcome-odds-core.js';

/**
 * Sibling Yes implied 0–1 aligned to `markets` (null = not in an exclusive family).
 * One collection-wide Yes/No family (skip Over/Under + nested strikes).
 * Only fills when sibling Yes sum overflows 1+ε (Polymarket NegRisk ~200% bug).
 */
export function exclusiveYesProbsForMarkets(markets, collection, readYesImplied) {
  const list = Array.isArray(markets) ? markets : [];
  const out = list.map(() => null);
  if (list.length < 2) return out;

  const members = [];
  for (let i = 0; i < list.length; i += 1) {
    const market = list[i];
    if (isMarketResolved(market, collection)) continue;
    // Self-built sports legs (四大金刚+波胆) are independent binaries — never exclusive-scale.
    const pricingKind = String(market?.pricingKind ?? market?.pricing_kind ?? '')
      .trim()
      .toLowerCase();
    if (pricingKind === 'sports_leg') continue;
    const marketType = String(market?.marketType ?? market?.market_type ?? '')
      .trim()
      .toLowerCase();
    if (
      marketType === 'match_winner' ||
      marketType === 'draw' ||
      marketType === 'handicap' ||
      marketType === 'over_under' ||
      marketType === 'correct_score'
    ) {
      continue;
    }
    const labels = parseMarketOutcomes(market?.outcomes);
    if (!isBinaryOutcomes(labels) || isSpreadOrTotalPair(labels)) continue;
    if (isNestedThresholdQuestion(market?.question)) continue;
    const prices = parseOutcomePrices(market?.outcomePrices);
    const fallbackYes = Number.isFinite(Number(prices[0])) ? toProb(prices[0]) : DEFAULT_HALF;
    const yes =
      typeof readYesImplied === 'function' ? toProb(readYesImplied(market, fallbackYes)) : fallbackYes;
    if (!Number.isFinite(yes)) continue;
    members.push({ i, yes });
  }
  if (members.length < 2) return out;

  const next = scaleExclusiveYesIfOverflow(members.map((m) => m.yes));
  if (!next) return out;
  members.forEach((mem, idx) => {
    out[mem.i] = next[idx];
  });
  return out;
}

/**
 * Scale sibling Yes implied prices on a collection of binary markets.
 * Mutates market.outcomePrices when a family has ≥2 members.
 */
export function applyCollectionExclusiveOutcomePrices(markets, collection, readYesImplied) {
  const list = Array.isArray(markets) ? markets : [];
  const yesProbs = exclusiveYesProbsForMarkets(list, collection, readYesImplied);
  let any = false;
  yesProbs.forEach((yesProb, i) => {
    if (yesProb == null) return;
    const { yes, no } = complementBinaryYes(yesProb);
    const encoded = serializeOutcomePrices([yes, no]);
    if (!encoded) return;
    if (list[i].outcomePrices === encoded) return;
    list[i].outcomePrices = encoded;
    any = true;
  });
  return any;
}
